import type { SelfPosition } from './gpsPeer';
import type { ProximityLocationBridge } from './locationBridge';

type SelfPositionListener = (position: SelfPosition | null) => void;

export type SelfPositionModuleOptions = {
  bridge: ProximityLocationBridge;
  onPosition?: (position: SelfPosition) => void;
  onLocationError?: (event: { message: string }) => void;
};

export interface SelfPositionModule {
  getSelfPosition(): SelfPosition | null;
  onSelfPositionChange(listener: SelfPositionListener): () => void;
  requestWhenInUseAuthorization(): Promise<void>;
  requestAlwaysAuthorization(): Promise<void>;
  startWatching(): Promise<void>;
  stopWatching(): Promise<void>;
}

export function createSelfPositionModule(options: SelfPositionModuleOptions): SelfPositionModule {
  const { bridge, onPosition, onLocationError } = options;
  let current: SelfPosition | null = null;
  const listeners = new Set<SelfPositionListener>();
  let locationUnsub: (() => void | Promise<void>) | null = null;
  let errorUnsub: (() => void | Promise<void>) | null = null;
  let errorLogged = false;

  function notify(): void {
    for (const cb of listeners) {
      cb(current);
    }
    if (current) {
      onPosition?.(current);
    }
  }

  function positionFromEvent(event: {
    lat: number;
    lng: number;
    accuracy: number;
    heading?: number;
    timestampMs: number;
  }): SelfPosition {
    return {
      lat: event.lat,
      lng: event.lng,
      accuracyMeters: event.accuracy,
      headingDegrees: Number.isFinite(event.heading) ? event.heading! : null,
      timestampMs: Math.floor(event.timestampMs),
    };
  }

  return {
    getSelfPosition() {
      return current;
    },

    onSelfPositionChange(listener) {
      listeners.add(listener);
      listener(current);
      return () => {
        listeners.delete(listener);
      };
    },

    async requestWhenInUseAuthorization() {
      await bridge.requestWhenInUseAuthorization();
    },

    async requestAlwaysAuthorization() {
      await bridge.requestAlwaysAuthorization();
    },

    async startWatching() {
      if (locationUnsub !== null) {
        return;
      }

      locationUnsub = await bridge.addLocationUpdateListener((event) => {
        current = positionFromEvent(event);
        notify();
      });

      errorUnsub = await bridge.addLocationErrorListener((event) => {
        onLocationError?.(event);
        if (!errorLogged) {
          errorLogged = true;
          console.warn('[GPS] self position watch error:', event.message);
        }
      });

      try {
        await bridge.startWatching();
      } catch (err) {
        if (!errorLogged) {
          errorLogged = true;
          const msg = err instanceof Error ? err.message : String(err);
          console.warn('[GPS] failed to start native location watch:', msg);
        }
      }
    },

    async stopWatching() {
      if (locationUnsub !== null) {
        await locationUnsub();
        locationUnsub = null;
      }
      if (errorUnsub !== null) {
        await errorUnsub();
        errorUnsub = null;
      }
      try {
        await bridge.stopWatching();
      } catch {
        /* ignore stop errors during teardown */
      }
    },
  };
}
